<template>
  <div id="assignment-performance">
    <el-row>
      <el-col>
        <el-table
          v-loading.fullscreen.lock="isModalLoading"
          :border="false"
          :cell-class-name="getCellClass"
          :data="performances"
          :empty-text="$t('common.no_values')"
          :max-height="600"
          style="width: 100%"
          class="table-ai-performance"
          @cell-mouse-enter="handleHoveringCell"
          @cell-mouse-leave="clearHoveringCell"
        >
          <el-table-column
            :label="$t('school_labels.student_name')"
            :sort-method="sortName"
            :sort-orders="getSortOrders"
            sortable
            fixed
            min-width="250"
          >
            <template slot-scope="scope">
              <div class="user-info">
                <div class="owner-info">
                  <el-link @click="setAssignmentDateAndRedirect(scope.row)" :underline="false">
                    <p>{{ scope.row.user_name }}</p>
                    <p>{{ scope.row.user_email }}</p>
                  </el-link>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            :render-header="handleHelpTooltip"
            :label="$t('ai_tutor.title.assignment_completed')"
            :sort-name="sortName"
            :sort-orders="getSortOrders"
            prop="date_complete"
            sortable
            min-width="270"
          >
            <template slot-scope="scope">
              <div class="wrap-time-completed">
                <span>{{ formatDateAndTime(scope.row.date_complete) }}</span>

                <div v-if="getChipText(scope)" class="tag-wrapper">
                  <chip-rounded v-show="true" :class="getChipClass(scope)" class="chip-rounded--small">
                    {{ getChipText(scope) }}
                  </chip-rounded>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            :label="
              `${formatText($t('classes.classes_details.assessment.table.overall_score'))} \n (${$t(
                'ai_tutor.highest'
              )})`
            "
            :sort-orders="getSortOrders"
            align="center"
            min-width="160"
            :render-header="handleHelpTooltip"
            prop="score"
            sortable
          >
            <template slot-scope="scope">
              <span>
                <span>{{ formatScore(scope.row.score) }}</span>
                <el-button
                  v-if="canShowButtonHistoryScore(scope.row)"
                  class="assignment-detail-btn"
                  @click.native.prevent="handleClickHistory(scope.row)"
                >
                  {{ $t("classes.classes_details.assessment.table.btn_history") }}
                </el-button>
              </span>
            </template>
          </el-table-column>
          <template>
            <el-table-column
              :label="
                `${formatText($t('ai_tutor.label_table_performance.pronunciation'))} \n (${$t('ai_tutor.highest')})`
              "
              align="center"
              min-width="150"
              :render-header="handleHelpTooltip"
              prop="pronunciation_score"
            >
              <template slot-scope="scope">
                <span class="text-tranform-cap">
                  {{ getTextScoreLevel(highestScore(scope).highest_pronunciation_score) }}
                </span>
              </template>
            </el-table-column>

            <el-table-column
              :label="
                `${formatText($t('ai_tutor.label_table_performance.vocabulary'))} \n (${$t('ai_tutor.highest')})`
              "
              align="center"
              min-width="150"
              :render-header="handleHelpTooltip"
              prop="vocabulary_score"
            >
              <template slot-scope="scope">
                <span class="text-tranform-cap">
                  {{ getTextScoreLevel(highestScore(scope).highest_vocabulary_score) }}
                </span>
              </template>
            </el-table-column>

            <el-table-column
              :label="`${formatText($t('ai_tutor.label_table_performance.grammar'))}  \n (${$t('ai_tutor.highest')})`"
              align="center"
              min-width="150"
              :render-header="handleHelpTooltip"
              prop="grammar_score"
            >
              <template slot-scope="scope">
                <span class="text-tranform-cap">
                  {{ getTextScoreLevel(highestScore(scope).highest_grammar) }}
                </span>
              </template>
            </el-table-column>

            <el-table-column
              :label="`${formatText($t('ai_tutor.goals_completed'))} \n (${$t('ai_tutor.highest')})`"
              align="center"
              min-width="160"
              :render-header="handleHelpTooltip"
              prop="goals_completed"
            >
              <template slot-scope="scope">
                <span> {{ goalCompleted(scope) }}</span>
              </template>
            </el-table-column>
            <el-table-column
              :label="`${formatText($t('ai_tutor.hints_used'))} \n (${$t('ai_tutor.avg')})`"
              align="center"
              min-width="150"
              :render-header="handleHelpTooltip"
              prop="hints_used"
            >
              <template slot-scope="scope">
                <span>
                  {{ formatHintUsed(highestScore(scope).avg_hints_used) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              :label="`${$t('ai_tutor.attempts')} \n (${$t('common.total')})`"
              align="center"
              min-width="149"
              :render-header="handleHelpTooltip"
              prop="attempts"
            >
              <template slot-scope="scope">
                <span>
                  {{ highestScore(scope).attempts }}
                </span>
              </template>
            </el-table-column>

            <el-table-column
              :label="`${$t('ai_tutor.time_spent')} \n (${$t('common.total')})`"
              align="center"
              min-width="150"
              fixed="right"
              sortable
              :sort-method="sortDuration"
              :sort-orders="getSortOrders"
              :render-header="handleHelpTooltip"
              prop="time_spent"
            >
              <template slot-scope="scope">
                <span>
                  {{ msToTime(highestScore(scope).time_spent * 1000) }}
                </span>
              </template>
            </el-table-column>
          </template>
        </el-table>
      </el-col>
    </el-row>
    <user-assignment-history-a-i
      :assignment-type="assignmentType"
      :dialogVisible="showUserHistoryScoreModal"
      :history="selectedUserHistoryScore"
      :title="$t('assignments.modals.assignment_history', { title: currentAssignment.name })"
      :user-info="selectedUser"
      @closed="showUserHistoryScoreModal = false"
    />
  </div>
</template>

<script>
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { mapActions, mapGetters } from "vuex";
import ChipRounded from "../common/ChipRounded.vue";
import { DATE_FORMAT_WITH_TIME, SUBMISSION_ON_TIME } from "@/constants/generic";
import { warningMessage } from "@/utils/messages";
import UserAssignmentHistoryAI from "@/components/assignments/UserAssignmentHistoryAI.vue";

export default {
  components: { UserAssignmentHistoryAI, ChipRounded },
  props: {
    performances: {
      type: Array,
      default: () => [{}],
    },
    dateFormat: {
      type: String,
      default: DATE_FORMAT_WITH_TIME,
    },
    studySets: {
      type: Array,
      default: () => [{}],
    },
    assignmentType: {
      type: String,
      default: "",
    },
    currAssignment: { type: Object, default: () => {} },
  },
  data() {
    return {
      showUserHistoryScoreModal: false,
      selectedUserHistoryScore: {},
      selectedUser: {},
      hoveringId: "",
      isModalLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      currentAssignment: "assignments/getCurrentAssignment",
      currentClass: "classes/getCurrentClass",
      currentOrganization: "organization/currentOrganization",
    }),
    getSortOrders() {
      return ["ascending", "descending"];
    },
  },
  methods: {
    ...mapActions({
      getUserAssignmentHistory: "assignments/getUserAssignmentHistory",
      setChartDate: "students/setChartDate",
    }),
    formatDate(date) {
      return isNaN(Date.parse(date)) ? date : format(new Date(date), this.dateFormat);
    },
    formatDateAndTime(date) {
      return isNaN(Date.parse(date)) ? date : format(new Date(date), this.dateFormat);
    },
    formatDateWithTimezone(date) {
      if (!date) return "--";
      let timeZone = this.currentOrganization.timezone;
      return utcToZonedTime(date, timeZone);
    },
    formatScore(score) {
      return score ? Math.round(score) + "%" : "--";
    },

    getCellClass() {
      return "";
    },
    sortName(a, b) {
      return a.user_name.toUpperCase().localeCompare(b.user_name.toUpperCase());
    },
    sortDuration(a, b) {
      if (!a.highest_score && !b.highest_score) return 0;
      if (!a.highest_score) return -1;
      if (!b.highest_score) return 1;
      if (a.highest_score[0].time_spent > b.highest_score[0].time_spent) return 1;
      return -1;
    },
    handleHelpTooltip(h, { column }) {
      return h("span", {}, [
        h(
          "el-tooltip",
          {
            attrs: {
              content: this.columnTooltipMap()[column.property],
              effect: "dark",
              placement: "top",
              popperClass: "tooltip-header-table-performance",
            },
          },
          [h("span", {}, [column.label])]
        ),
      ]);
    },
    columnTooltipMap() {
      return {
        date_complete: this.$t("ai_tutor.tooltip.assignment_completed"),
        score: this.$t("ai_tutor.tooltip.overall_score"),
        pronunciation_score: this.$t("ai_tutor.tooltip.pronunciation_score"),
        vocabulary_score: this.$t("ai_tutor.tooltip.vocabulary_score"),
        grammar_score: this.$t("ai_tutor.tooltip.grammar_score"),
        goals_completed: this.$t("ai_tutor.tooltip.goals_completed"),
        hints_used: this.$t("ai_tutor.tooltip.hints_used"),
        attempts: this.$t("ai_tutor.tooltip.attempts"),
        time_spent: this.$t("ai_tutor.tooltip.time_spent"),
      };
    },
    handleHoveringCell(row) {
      this.hoveringId = row.user_id;
    },
    clearHoveringCell() {
      this.hoveringId = "";
    },
    canShowButtonHistoryScore(row) {
      const assignmentTypeShowHistory = ["read_aloud", "ai_tutor"];

      return (
        assignmentTypeShowHistory.includes(this.assignmentType) &&
        row.score &&
        row.score > 0 &&
        row.user_id === this.hoveringId
      );
    },
    handleClickHistory(row) {
      if (this.assignmentType === "ai_tutor") {
        this.handleShowUserAIHistoryScore(row);

        return;
      }

      this.handleShowUserHistoryScore(row);
    },
    async handleShowUserHistoryScore(row) {
      this.isModalLoading = true;
      this.selectedUserHistoryScore = {};
      this.selectedUser = row;
      try {
        this.selectedUserHistoryScore = await this.getUserAssignmentHistory({
          userId: row.user_id,
          assignmentId: this.currentAssignment.id,
        });
        this.isModalLoading = false;
        this.showUserHistoryScoreModal = true;
      } catch {
        this.isModalLoading = false;
        return warningMessage(this.$t("common.generic_error_message"))(this);
      }
    },
    async handleShowUserAIHistoryScore(row) {
      this.isModalLoading = true;
      this.selectedUserHistoryScore = {};
      this.selectedUser = row;
      try {
        //call to api
        this.selectedUserHistoryScore = await this.getUserAssignmentHistory({
          userId: row.user_id,
          assignmentId: this.currentAssignment.id,
        });

        this.isModalLoading = false;
        this.showUserHistoryScoreModal = true;
      } catch {
        this.isModalLoading = false;
        return warningMessage(this.$t("common.generic_error_message"))(this);
      }
    },
    setAssignmentDateAndRedirect(row) {
      this.setChartDate({
        startDate: this.formatDateWithTimezone(this.currentAssignment.start_date),
        endDate: this.formatDateWithTimezone(this.currentAssignment.due_date),
      });

      this.$router.push({
        name: "student-details",
        params: {
          organizationId: this.currentClass.organization_id,
          studentId: row.user_id,
        },
      });
    },
    highestScore(scope) {
      if (!scope.row.highest_score || !scope.row.highest_score.length) return {};

      return scope.row.highest_score[0];
    },
    getTextScoreLevel(scoreText) {
      if (!scoreText) return "--";
      switch (scoreText.toLowerCase()) {
        case "Elementary":
          return this.$t("ai_tutor.score_range.elementary");
        case "intermediate":
          return this.$t("ai_tutor.score_range.intermediate");
        case "advanced":
          return this.$t("ai_tutor.score_range.advanced");
        default:
          return scoreText;
      }
    },
    goalCompleted(scope) {
      if (!scope.row.highest_score || !scope.row.highest_score.length || !scope.row.highest_score[0].total_goals)
        return "--";

      return `${scope.row.highest_score[0].goals_completed}/${scope.row.highest_score[0].total_goals}`;
    },
    getChipText(scope) {
      const nowTime = new Date().getTime();
      const dueDateTime = new Date(this.currAssignment.due_date).getTime();

      const SUBMISSION_ON_TIME = "Ontime";
      const SUBMISSION_LATE = "Late";
      const SUBMISSION_MISSED = "Missed";

      let submission_status = "";
      switch (true) {
        case (scope.row.date_complete === "Not Started" || scope.row.date_complete === "In Progress") &&
          dueDateTime < nowTime:
          submission_status = SUBMISSION_MISSED;
          break;
        case new Date(scope.row.date_complete).getTime() <= dueDateTime:
          submission_status = SUBMISSION_ON_TIME;
          break;
        case new Date(scope.row.date_complete).getTime() > dueDateTime:
          submission_status = SUBMISSION_LATE;
          break;
        default:
          submission_status = "";
          break;
      }

      return submission_status;
    },
    getChipClass(scope) {
      const nowTime = new Date().getTime();
      const dueDateTime = new Date(this.currAssignment.due_date).getTime();

      const SUBMISSION_ON_TIME = "On time";
      const SUBMISSION_LATE = "Late";
      const SUBMISSION_MISSED = "Missed";

      let submission_status = "";
      switch (true) {
        case (scope.row.date_complete === "Not Started" || scope.row.date_complete === "In Progress") &&
          dueDateTime < nowTime:
          submission_status = SUBMISSION_MISSED;
          break;
        case new Date(scope.row.date_complete).getTime() <= dueDateTime:
          submission_status = SUBMISSION_ON_TIME;
          break;
        case new Date(scope.row.date_complete).getTime() > dueDateTime:
          submission_status = SUBMISSION_LATE;
          break;
        default:
          submission_status = "";
          break;
      }

      switch (submission_status) {
        case SUBMISSION_MISSED:
          return "chip-rounded--red";
        case SUBMISSION_LATE:
          return "chip-rounded--orange";
        default:
          return "";
      }
    },
    msToTime(duration) {
      if (!duration) return "--";
      let seconds = Math.floor((duration / 1000) % 60);
      let minutes = Math.floor((duration / (1000 * 60)) % 60);
      let hours = Math.floor((duration / (1000 * 60 * 60)) % 24);

      hours = hours < 10 ? "0" + hours : hours;
      minutes = minutes < 10 ? "0" + minutes : minutes;
      seconds = seconds < 10 ? "0" + seconds : seconds;

      return hours + ":" + minutes + ":" + seconds;
    },

    formatText(text) {
      if (!text) return "";
      return text.replace(/\(\w*\)/gm, "").trim();
    },
    formatHintUsed(number) {
      if (number === undefined) return "";
      return Math.round(number);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../scss/_colors.scss";
#assignment-performance {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  box-sizing: border-box;
  border-radius: 8px;
  padding: 0px 0px;
  margin-bottom: 24px;
  overflow: hidden;

  ::v-deep thead .cell {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.date-completed {
  display: flex;
  align-items: center;
  .date {
    width: 100px;
  }
  .tag-wrapper {
    min-width: 50px;
    margin-left: 10px;
  }
}

.score-cell {
  padding-left: 10px;
}

::v-deep .el-table {
  thead {
    text-transform: inherit;
  }
  .el-table__row {
    height: 80px;
    [class*="el-table_1_column"] {
      &.el-table__column--custom {
        border-top: none;
        .cell {
          padding-top: 0;
          padding-bottom: 0;
        }
      }
    }
  }
}

.user-info {
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 14px;
  color: $darker-blue;
  .owner-info {
    p {
      font-weight: 500;
      font-size: 16px;
      text-transform: capitalize;
      color: $hyperlink;
      margin: 0;
      text-align: initial;
      display: flex;
      align-items: left;
      &:last-of-type {
        font-size: 14px;
        color: $light-blue-2;
        text-transform: none;
        line-height: 34px;
      }
    }
  }
}

.text-tranform-cap {
  text-transform: capitalize;
}

.wrap-time-completed {
  display: flex;
  gap: 4px;
}
</style>

<style lang="scss">
.table-ai-performance {
  .cell {
    font-weight: 400;

    .el-tooltip {
      white-space: pre-wrap;
    }
  }
}

.tooltip-header-table-performance {
  max-width: 372px !important;
}
</style>
